import { useState } from 'react';
import { MAX_NOTE, charsLeft, cleanNote, editedLine, noteChanged, showCounter } from './siteNotesModel';

const DEFAULT_TONE = { ink: 'var(--c-ink)', muted: 'var(--c-ink-mute)', line: 'var(--c-line)', surface: 'var(--c-field)', solidText: 'var(--c-solid-text)' };

// One visit's progress note. Read-only unless onSave is given (builder timeline
// and the owner page); stakeholder links only ever see the text.
export default function ProgressNote({ note, onSave, tone, canAdd = true }) {
  const t = { ...DEFAULT_TONE, ...(tone || {}) };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const has = Boolean(note && note.text);
  // Tertiary text buttons, 44px tall for touch.
  const btn = { border: 'none', background: 'none', padding: '0 4px', margin: '0 -4px', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 500, color: t.ink, cursor: 'pointer', minHeight: 44, minWidth: 44, justifyContent: 'center', display: 'inline-flex', alignItems: 'center', borderRadius: 2 };

  const open = () => { setDraft(has ? note.text : ''); setError(''); setEditing(true); };
  const save = async (text) => {
    setBusy(true);
    setError('');
    try {
      await onSave(text);
      setEditing(false);
    } catch (e) {
      setError((e && e.message) || 'Couldn’t save the note. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (editing && onSave) {
    const left = charsLeft(draft);
    return (
      <div data-testid="progress-note-editor" style={{ marginTop: 16, minWidth: 0, maxWidth: 640 }}>
        <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: t.ink, marginBottom: 8 }}>
          Progress note
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={MAX_NOTE} rows={4} autoFocus
            placeholder="What changed on site since the last visit."
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '12px 14px', border: `1px solid ${t.line}`, borderRadius: 2,
              background: t.surface, color: t.ink, fontFamily: 'inherit', fontSize: 16, lineHeight: 1.45, resize: 'vertical', minWidth: 0 }} />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
          <button type="button" disabled={busy || !noteChanged(note, draft) || left < 0} onClick={() => save(cleanNote(draft))}
            style={{ padding: '0 20px', minHeight: 44, border: 'none', borderRadius: 2, background: t.solid || t.ink, color: t.solidText, fontFamily: 'inherit', fontSize: 14, fontWeight: 500, cursor: busy ? 'wait' : 'pointer' }}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" onClick={() => setEditing(false)} disabled={busy} style={btn}>Cancel</button>
          {has ? <button type="button" onClick={() => save('')} disabled={busy} style={btn}>Remove note</button> : null}
          {showCounter(draft) ? <span style={{ fontSize: 11, color: left < 0 ? 'var(--c-bad)' : t.muted }}>{left} left</span> : null}
        </div>
        <p style={{ fontSize: 12.5, color: t.muted, margin: '4px 0 0' }}>Shows on your share links.</p>
        {error ? <p role="alert" style={{ fontSize: 12, color: 'var(--c-bad)', margin: '4px 0 0' }}>{error}</p> : null}
      </div>
    );
  }

  if (!has) {
    return onSave && canAdd ? (
      <div style={{ marginTop: 8 }}>
        <button type="button" onClick={open} style={btn}>Add progress note</button>
      </div>
    ) : null;
  }

  const edited = editedLine(note);
  return (
    <div data-testid="progress-note" style={{ marginTop: 16, paddingLeft: 16, borderLeft: `1px solid ${t.line}`, minWidth: 0, maxWidth: 680 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: t.muted }}>Progress note</div>
      <p style={{ margin: '4px 0 0', fontSize: 15, lineHeight: 1.6, color: t.ink, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note.text}</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', minHeight: 24, marginTop: 4 }}>
        {edited ? <span style={{ fontSize: 12.5, color: t.muted }}>{edited}</span> : null}
        {onSave ? <button type="button" onClick={open} style={btn}>Edit</button> : null}
      </div>
    </div>
  );
}
